/**
 * Live Quiz Zustand Store
 *
 * Central state management for live quiz sessions (both host and player views)
 */

import { create } from 'zustand';
import type {
  LiveQuizStatus,
  LiveQuizSettings,
  LiveQuizQuestion,
  LiveQuizParticipant,
  LeaderboardEntry,
  AnswerStats,
} from '../types/liveQuiz';

// ============ HOST STORE ============

interface HostState {
  // Connection
  connected: boolean;
  connecting: boolean;
  error: string | null;

  // Session
  sessionId: string | null;
  gamePin: string | null;
  status: LiveQuizStatus;
  settings: LiveQuizSettings | null;

  // Questions
  currentQuestionIndex: number;
  totalQuestions: number;
  currentQuestion: LiveQuizQuestion | null;
  timeRemaining: number;

  // Participants
  participants: LiveQuizParticipant[];
  readyCount: number;

  // Stats
  answerStats: AnswerStats | null;
  leaderboard: LeaderboardEntry[];
  answeredCount: number;
  correctAnswer: unknown | null; // Revealed after question ends
}

interface HostActions {
  // Connection
  setConnected: (connected: boolean) => void;
  setConnecting: (connecting: boolean) => void;
  setError: (error: string | null) => void;

  // Session
  setSession: (sessionId: string, gamePin: string, settings: LiveQuizSettings, totalQuestions: number) => void;
  setStatus: (status: LiveQuizStatus) => void;

  // Questions
  setQuestion: (question: LiveQuizQuestion, index: number, timeSeconds: number) => void;
  setTimeRemaining: (time: number) => void;

  // Participants
  setParticipants: (participants: LiveQuizParticipant[]) => void;
  addParticipant: (participant: LiveQuizParticipant) => void;
  removeParticipant: (participantId: string) => void;
  updateParticipantReady: (participantId: string, isReady: boolean, readyCount: number) => void;

  // Stats
  setAnswerStats: (stats: AnswerStats) => void;
  setLeaderboard: (leaderboard: LeaderboardEntry[]) => void;
  setAnsweredCount: (count: number) => void;
  setCorrectAnswer: (answer: unknown | null) => void;

  // Reset
  reset: () => void;
}

const initialHostState: HostState = {
  connected: false,
  connecting: false,
  error: null,
  sessionId: null,
  gamePin: null,
  status: 'lobby',
  settings: null,
  currentQuestionIndex: -1,
  totalQuestions: 0,
  currentQuestion: null,
  timeRemaining: 0,
  participants: [],
  readyCount: 0,
  answerStats: null,
  leaderboard: [],
  answeredCount: 0,
  correctAnswer: null,
};

export const useHostStore = create<HostState & HostActions>((set) => ({
  ...initialHostState,

  setConnected: (connected) => set({ connected, connecting: false }),
  setConnecting: (connecting) => set({ connecting }),
  setError: (error) => set({ error }),

  setSession: (sessionId, gamePin, settings, totalQuestions) =>
    set({ sessionId, gamePin, settings, totalQuestions }),

  setStatus: (status) => set({ status }),

  setQuestion: (question, index, timeSeconds) =>
    set({
      currentQuestion: question,
      currentQuestionIndex: index,
      timeRemaining: timeSeconds,
      answerStats: null,
      answeredCount: 0,
      correctAnswer: null, // Reset for new question
    }),

  setTimeRemaining: (time) => set({ timeRemaining: time }),

  setParticipants: (participants) => set({ participants }),

  addParticipant: (participant) =>
    set((state) => ({
      participants: [...state.participants.filter(p => p.id !== participant.id), participant],
    })),

  removeParticipant: (participantId) =>
    set((state) => ({
      participants: state.participants.filter((p) => p.id !== participantId),
    })),

  updateParticipantReady: (participantId, isReady, readyCount) =>
    set((state) => ({
      participants: state.participants.map((p) =>
        p.id === participantId ? { ...p, isReady } : p
      ),
      readyCount,
    })),

  setAnswerStats: (stats) => set({ answerStats: stats }),
  setLeaderboard: (leaderboard) => set({ leaderboard }),
  setAnsweredCount: (count) => set({ answeredCount: count }),
  setCorrectAnswer: (answer) => set({ correctAnswer: answer }),

  reset: () => set(initialHostState),
}));

// ============ PLAYER STORE ============

interface PlayerState {
  // Connection
  connected: boolean;
  connecting: boolean;
  error: string | null;

  // Session
  sessionId: string | null;
  gamePin: string | null;
  status: LiveQuizStatus;
  settings: LiveQuizSettings | null;

  // Player info
  participantId: string | null;
  nickname: string | null;
  avatar: string | null;

  // Questions
  currentQuestionIndex: number;
  totalQuestions: number;
  currentQuestion: Omit<LiveQuizQuestion, 'correctAnswer'> | null;
  timeRemaining: number;

  // My state
  isReady: boolean;
  myAnswer: unknown | null;
  myScore: number;
  myRank: number;
  myStreak: number;

  // Last answer result
  lastResult: {
    correct: boolean;
    points: number;
    totalScore: number;
    streak: number;
    rank: number;
    responseTimeMs: number;
  } | null;

  // Game state
  participantCount: number;
  answeredCount: number;
  correctAnswer: unknown | null;
  leaderboard: LeaderboardEntry[];
}

interface PlayerActions {
  // Connection
  setConnected: (connected: boolean) => void;
  setConnecting: (connecting: boolean) => void;
  setError: (error: string | null) => void;

  // Session
  setSession: (
    sessionId: string,
    gamePin: string,
    settings: LiveQuizSettings,
    totalQuestions: number,
    participantId: string,
    nickname: string,
    avatar?: string
  ) => void;
  setStatus: (status: LiveQuizStatus) => void;

  // Questions
  setQuestion: (question: Omit<LiveQuizQuestion, 'correctAnswer'>, index: number, timeSeconds: number) => void;
  setTimeRemaining: (time: number) => void;

  // My state
  setIsReady: (ready: boolean) => void;
  setMyAnswer: (answer: unknown) => void;
  setLastResult: (result: PlayerState['lastResult']) => void;

  // Game updates
  setParticipantCount: (count: number) => void;
  setAnsweredCount: (count: number) => void;
  setCorrectAnswer: (answer: unknown) => void;
  setLeaderboard: (leaderboard: LeaderboardEntry[]) => void;

  // Reset
  reset: () => void;
  resetForNextQuestion: () => void;
}

const initialPlayerState: PlayerState = {
  connected: false,
  connecting: false,
  error: null,
  sessionId: null,
  gamePin: null,
  status: 'lobby',
  settings: null,
  participantId: null,
  nickname: null,
  avatar: null,
  currentQuestionIndex: -1,
  totalQuestions: 0,
  currentQuestion: null,
  timeRemaining: 0,
  isReady: false,
  myAnswer: null,
  myScore: 0,
  myRank: 0,
  myStreak: 0,
  lastResult: null,
  participantCount: 0,
  answeredCount: 0,
  correctAnswer: null,
  leaderboard: [],
};

export const usePlayerStore = create<PlayerState & PlayerActions>((set) => ({
  ...initialPlayerState,

  setConnected: (connected) => set({ connected, connecting: false }),
  setConnecting: (connecting) => set({ connecting }),
  setError: (error) => set({ error }),

  setSession: (sessionId, gamePin, settings, totalQuestions, participantId, nickname, avatar) =>
    set({
      sessionId,
      gamePin,
      settings,
      totalQuestions,
      participantId,
      nickname,
      avatar,
    }),

  setStatus: (status) => set({ status }),

  setQuestion: (question, index, timeSeconds) =>
    set({
      currentQuestion: question,
      currentQuestionIndex: index,
      timeRemaining: timeSeconds,
      myAnswer: null,
      lastResult: null,
      correctAnswer: null,
    }),

  setTimeRemaining: (time) => set({ timeRemaining: time }),

  setIsReady: (ready) => set({ isReady: ready }),
  setMyAnswer: (answer) => set({ myAnswer: answer }),

  setLastResult: (result) =>
    set({
      lastResult: result,
      myScore: result?.totalScore ?? 0,
      myRank: result?.rank ?? 0,
      myStreak: result?.streak ?? 0,
    }),

  setParticipantCount: (count) => set({ participantCount: count }),
  setAnsweredCount: (count) => set({ answeredCount: count }),
  setCorrectAnswer: (answer) => set({ correctAnswer: answer }),
  setLeaderboard: (leaderboard) => set({ leaderboard }),

  reset: () => set(initialPlayerState),

  resetForNextQuestion: () =>
    set({
      myAnswer: null,
      lastResult: null,
      correctAnswer: null,
      answeredCount: 0,
    }),
}));

// ============ SELECTORS ============

export const selectActiveParticipants = (state: HostState) =>
  state.participants.filter((p) => p.status === 'connected');

export const selectParticipantById = (state: HostState, id: string) =>
  state.participants.find((p) => p.id === id);

export const selectAnswerPercentage = (state: HostState) => {
  const active = state.participants.filter((p) => p.status === 'connected').length;
  if (active === 0) return 0;
  return Math.round((state.answeredCount / active) * 100);
};

export const selectMyPosition = (state: PlayerState) => {
  if (!state.participantId || state.leaderboard.length === 0) return null;
  const entry = state.leaderboard.find((e) => e.participantId === state.participantId);
  return entry || null;
};

export const selectIsWaiting = (state: PlayerState) =>
  state.myAnswer !== null && state.status === 'question';

export const selectCanAnswer = (state: PlayerState) =>
  state.status === 'question' && state.myAnswer === null && state.timeRemaining > 0;
